import { useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { signIn } from "../lib/auth-client.ts";
import { Button } from "@/components/ui/button";
import { BrandMark } from "@/components/vroom/brand-mark";
import { FormField } from "@/components/vroom/form-field";

export const Route = createFileRoute("/sign-in")({
  component: SignInPage,
});

function SignInPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  async function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError(null);
    setPending(true);
    const result = await signIn.email({ email, password });
    setPending(false);
    if (result.error) {
      setError(result.error.message ?? t("sign-in.form.generic-error"));
      return;
    }
    const data = result.data as { twoFactorRedirect?: boolean } | undefined;
    if (data?.twoFactorRedirect) {
      await navigate({ to: "/two-factor" });
      return;
    }
    await navigate({ to: "/dashboard" });
  }

  return (
    <main className="mx-auto mt-20 grid w-full max-w-sm gap-6 px-4">
      <div className="flex items-center gap-2">
        <BrandMark />
        <span
          className="text-[18px] font-semibold text-ink-900"
          style={{ fontFamily: "var(--font-display)" }}
        >
          {t("common.brand.label")}
        </span>
      </div>
      <h1
        className="text-[22px] font-semibold text-ink-900"
        style={{ fontFamily: "var(--font-display)" }}
      >
        {t("sign-in.title")}
      </h1>
      <form onSubmit={onSubmit} className="grid gap-4">
        <FormField
          id="sign-in-email"
          label={t("sign-in.form.email.label")}
          inputProps={{
            type: "email",
            value: email,
            onChange: (e) => setEmail(e.currentTarget.value),
            required: true,
            autoComplete: "email",
          }}
        />
        <FormField
          id="sign-in-password"
          label={t("sign-in.form.password.label")}
          error={error ?? undefined}
          inputProps={{
            type: "password",
            value: password,
            onChange: (e) => setPassword(e.currentTarget.value),
            required: true,
            autoComplete: "current-password",
          }}
        />
        <Button type="submit" variant="primary" loading={pending} className="justify-center">
          {t("sign-in.form.submit.label")}
        </Button>
      </form>
    </main>
  );
}
